<template>

    <div class="vs-page">

        <!-- 1. En-tête + filtres -->
        <div class="ds-page-head">
            <div>
                <h1 class="ds-page-head__title">Valorisation du stock</h1>
                <p class="ds-page-head__sub">
                    Situation au {{ dateDuJour }}<template v-if="derniereMaj">, {{ derniereMaj }}</template>
                    · {{ magasin || 'tous les magasins' }}
                    <template v-if="charge"> · {{ formatNombre(lignesMagasin.length) }} lignes de stock</template>
                </p>
            </div>
            <div class="vs-filters">
                <select class="vs-input" v-model="magasin" aria-label="Magasin">
                    <option value="">Tous les magasins</option>
                    <option v-for="m in magasins" :key="m" :value="m">{{ m }}</option>
                </select>
                <button type="button" class="vs-btn" title="Actualiser" :disabled="chargement" @click="charger(true)">
                    <i class="ri-refresh-line" :class="{ 'vs-spin': chargement }"></i>
                </button>
                <button type="button" class="vs-btn vs-btn--xls" title="Exporter la sélection" :disabled="!lignesFiltrees.length" @click="exporterExcel">
                    <i class="ri-file-excel-2-line"></i> Excel
                </button>
            </div>
        </div>

        <!-- Données servies par le cache 5 min -->
        <bandeaucachetb v-if="depuisCache" quoi="la valorisation du stock" verbe="est gardée"
                        manque="Les quantités et les prix modifiés depuis n'y figurent pas encore."
                        :heure="heureCache" @actualiser="charger(true)"></bandeaucachetb>

        <!-- 2. KPI -->
        <div class="vs-kpis">
            <template v-if="charge">
                <div class="vs-kpi vs-kpi--main">
                    <div class="vs-kpi__lbl">Valeur au prix d'achat <i class="ri-safe-2-line"></i></div>
                    <div class="vs-kpi__val">{{ formatMoney(totaux.va) }} <small>F CFA</small></div>
                    <div class="vs-kpi__sub">Ce que le stock a coûté (valeur comptable)</div>
                </div>
                <div class="vs-kpi">
                    <div class="vs-kpi__lbl">Valeur au prix de vente <i class="ri-price-tag-3-line vs-i-vio"></i></div>
                    <div class="vs-kpi__val">{{ formatMoney(totaux.vv) }} <small>F</small></div>
                    <div class="vs-kpi__sub">Ce que le stock rapporterait vendu au prix affiché</div>
                </div>
                <div class="vs-kpi">
                    <div class="vs-kpi__lbl">Marge latente <i class="ri-funds-line vs-i-grn"></i></div>
                    <div class="vs-kpi__val">{{ formatMoney(totaux.vv - totaux.va) }} <small>F</small></div>
                    <div class="vs-kpi__sub"><b class="vs-pos">{{ formatPourcent(totaux.vv ? (totaux.vv - totaux.va) / totaux.vv * 100 : 0) }} %</b> du prix de vente</div>
                    <div class="vs-split">
                        <i :style="{ width: (totaux.vv ? Math.min(100, totaux.va / totaux.vv * 100) : 0) + '%', background: '#f59e0b' }"></i>
                        <i :style="{ flex: 1, background: '#059669' }"></i>
                    </div>
                </div>
                <div class="vs-kpi">
                    <div class="vs-kpi__lbl">Articles en stock <i class="ri-archive-line vs-i-blue"></i></div>
                    <div class="vs-kpi__val">{{ formatNombre(totaux.refs) }} <small>références</small></div>
                    <div class="vs-kpi__sub">{{ formatNombre(totaux.q) }} unités au total</div>
                </div>
                <div class="vs-kpi vs-kpi--warn" role="button" tabindex="0" @click="choisirPastille('sanspa')" @keyup.enter="choisirPastille('sanspa')">
                    <div class="vs-kpi__lbl">Données à compléter <i class="ri-error-warning-line vs-i-amb"></i></div>
                    <div class="vs-kpi__val">{{ formatNombre(totaux.sanspa + totaux.sanspv + totaux.neg) }} <small>lignes</small></div>
                    <div class="vs-kpi__sub">{{ totaux.sanspa }} sans prix d'achat · {{ totaux.sanspv }} sans prix de vente · {{ totaux.neg }} stock négatif</div>
                </div>
            </template>
            <template v-else>
                <div v-for="n in 5" :key="n" class="vs-kpi">
                    <div class="vs-skel" style="width:45%"></div>
                    <div class="vs-skel vs-skel--lg" style="width:70%"></div>
                    <div class="vs-skel" style="width:85%"></div>
                </div>
            </template>
        </div>

        <!-- 3. Graphiques -->
        <div class="vs-g3">
            <div class="vs-card">
                <div class="vs-card__head"><h2><i class="ri-store-2-line"></i> Valeur par magasin <span>· prix d'achat</span></h2></div>
                <div class="vs-card__body">
                    <apexcharts v-if="charge && parMagasin.length" type="bar" height="230" :options="optionsMagasin" :series="[{ name: 'Valeur', data: parMagasin.map(x => x.v) }]"></apexcharts>
                    <div v-else-if="charge" class="vs-empty"><i class="ri-store-2-line"></i>Aucun stock valorisé.</div>
                    <div v-else class="vs-skel vs-skel--chart"></div>
                </div>
            </div>
            <div class="vs-card">
                <div class="vs-card__head"><h2><i class="ri-folder-chart-line"></i> Valeur par catalogue</h2></div>
                <div class="vs-card__body">
                    <apexcharts v-if="charge && parCatalogue.length" type="donut" height="230" :options="optionsCatalogue" :series="parCatalogue.map(x => x.v)"></apexcharts>
                    <div v-else-if="charge" class="vs-empty"><i class="ri-folder-chart-line"></i>Aucun stock valorisé.</div>
                    <div v-else class="vs-skel vs-skel--chart"></div>
                </div>
            </div>
            <div class="vs-card">
                <div class="vs-card__head"><h2><i class="ri-bar-chart-grouped-line"></i> Concentration (ABC)</h2></div>
                <div class="vs-card__body">
                    <div v-if="charge" class="vs-abc">
                        <div v-for="c in syntheseAbc" :key="c.cle" class="vs-abc__row">
                            <span class="vs-abc__l" :style="{ background: couleurAbc[c.cle] }">{{ c.cle }}</span>
                            <div>
                                <div class="vs-abc__t"><b>{{ c.n }} lignes ({{ c.partN }} %)</b> = <b>{{ c.partV }} %</b> de la valeur</div>
                                <div class="vs-abc__t vs-abc__t--sm">{{ c.texte }}</div>
                                <div class="vs-abc__bar"><i :style="{ width: c.partV + '%', background: couleurAbc[c.cle] }"></i></div>
                            </div>
                        </div>
                    </div>
                    <template v-else><div v-for="n in 6" :key="n" class="vs-skel"></div></template>
                </div>
            </div>
        </div>

        <!-- 4. Tableau -->
        <div class="vs-card">
            <div class="vs-toolbar">
                <div class="vs-search">
                    <i class="ri-search-line"></i>
                    <input ref="recherche" type="search" v-model="recherche" placeholder="Rechercher un article, un magasin, un catalogue..." @keyup.esc="recherche = ''">
                    <kbd v-if="!recherche">/</kbd>
                </div>
                <div class="vs-seg" role="group" aria-label="Regroupement">
                    <button v-for="g in regroupements" :key="g.code" type="button"
                            :class="{ 'vs-seg--on': regroupement === g.code }"
                            @click="regroupement = g.code; tri = 'va'; sens = -1; page = 1">{{ g.label }}</button>
                </div>
                <select class="vs-input" v-model.number="parPage" aria-label="Lignes par page" @change="page = 1">
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
            </div>

            <div class="vs-chips">
                <button v-for="c in pastilles" :key="c.code" type="button" class="vs-chip"
                        :class="[c.cls, { 'vs-chip--on': pastille === c.code }]"
                        @click="choisirPastille(c.code)">
                    {{ c.label }} <b>{{ compteurs[c.code] }}</b>
                </button>
                <label class="vs-toggle"><input type="checkbox" v-model="masquerZero" @change="page = 1"> Masquer les stocks à 0</label>
                <span class="vs-chips__sep"></span>
                <span class="vs-resume">{{ lignesFiltrees.length }} résultat{{ lignesFiltrees.length > 1 ? 's' : '' }}<template v-if="recherche"> pour « {{ recherche }} »</template></span>
            </div>

            <template v-if="charge">
                <div class="vs-table-wrap vs-only-desktop">
                    <table>
                        <thead>
                        <tr>
                            <th v-for="c in colonnes" :key="c.k" class="th"
                                :class="[{ 'th--right': c.r, 'th--sorted': tri === c.tri }, c.g ? 'th--' + c.g : '']"
                                @click="trier(c.tri)">
                                {{ c.k === 'lib' && regroupement === 'cat' ? 'Catalogue' : c.l }}<span class="th__s">{{ tri === c.tri ? (sens < 0 ? ' ↓' : ' ↑') : ' ↕' }}</span>
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="!lignesFiltrees.length">
                            <td :colspan="colonnes.length">
                                <div class="vs-empty"><i class="ri-search-eye-line"></i><b>Aucune ligne ne correspond.</b><br>Changez la recherche, la pastille ou le magasin.</div>
                            </td>
                        </tr>
                        <tr v-for="g in lignesPage" :key="g.cle" class="vs-row">
                            <td class="td">
                                <div class="vs-art">
                                    <span class="vs-abcb" :style="{ background: couleurAbc[g.abc] }" :title="'Classe ' + g.abc">{{ g.abc }}</span>
                                    <div>
                                        <div class="vs-art__name">
                                            <span v-html="surligner(g.lib)"></span>
                                            <span v-if="g.sanspa" class="vs-tag vs-tag--pa">PA manquant</span>
                                            <span v-if="g.sanspv" class="vs-tag vs-tag--bad">PV manquant</span>
                                            <span v-if="g.neg" class="vs-tag vs-tag--bad">stock négatif</span>
                                        </div>
                                        <div class="vs-art__meta" v-html="surligner(g.meta)"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="td td--right" :class="{ 'vs-neg': g.q < 0 }">{{ formatNombre(g.q) }}</td>
                            <td class="td td--right vs-ca"><span :class="{ 'vs-faint': !g.pa }">{{ formatMoney(g.pa) }}</span></td>
                            <td class="td td--right vs-ca"><b>{{ formatMoney(g.va) }}</b></td>
                            <td class="td td--right vs-cv"><span :class="{ 'vs-faint': !g.pv }">{{ formatMoney(g.pv) }}</span></td>
                            <td class="td td--right vs-cv"><b>{{ formatMoney(g.vv) }}</b></td>
                            <td class="td td--right">
                                <span :class="g.ml < 0 ? 'vs-neg' : 'vs-pos'">{{ formatMoney(g.ml) }}</span>
                                <div class="vs-art__meta">{{ formatPourcent(g.vv ? g.ml / g.vv * 100 : 0) }} %</div>
                            </td>
                            <td class="td td--right">
                                <span class="vs-part">{{ formatPourcent(totaux.va ? g.va / totaux.va * 100 : 0) }} %<span><i :style="{ width: Math.max(2, Math.min(100, totaux.va ? g.va / totaux.va * 300 : 0)) + '%' }"></i></span></span>
                            </td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <td class="td"><span class="vs-foot__lbl">Total de la sélection</span>{{ lignesFiltrees.length }} ligne{{ lignesFiltrees.length > 1 ? 's' : '' }}</td>
                            <td class="td td--right"><span class="vs-foot__lbl">Stock</span>{{ formatNombre(totauxSelection.q) }}</td>
                            <td class="td"></td>
                            <td class="td td--right"><span class="vs-foot__lbl">Valeur achat</span>{{ formatMoney(totauxSelection.va) }}</td>
                            <td class="td"></td>
                            <td class="td td--right"><span class="vs-foot__lbl">Valeur vente</span>{{ formatMoney(totauxSelection.vv) }}</td>
                            <td class="td td--right"><span class="vs-foot__lbl">Marge latente</span>{{ formatMoney(totauxSelection.vv - totauxSelection.va) }}</td>
                            <td class="td td--right"><span class="vs-foot__lbl">Part</span>{{ formatPourcent(totaux.va ? totauxSelection.va / totaux.va * 100 : 0) }} %</td>
                        </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- cartes (mobile) -->
                <div class="vs-mcards vs-only-mobile">
                    <div v-if="!lignesFiltrees.length" class="vs-empty"><i class="ri-search-eye-line"></i>Aucune ligne ne correspond.</div>
                    <div v-for="g in lignesPage" :key="g.cle" class="vs-mcard">
                        <div class="vs-art">
                            <span class="vs-abcb" :style="{ background: couleurAbc[g.abc] }">{{ g.abc }}</span>
                            <div>
                                <div class="vs-art__name"><span v-html="surligner(g.lib)"></span></div>
                                <div class="vs-art__meta">{{ g.meta }} · stock {{ formatNombre(g.q) }}</div>
                            </div>
                        </div>
                        <div class="vs-mcard__g">
                            <div>Valeur achat<b>{{ formatMoney(g.va) }}</b></div>
                            <div>Valeur vente<b>{{ formatMoney(g.vv) }}</b></div>
                            <div>Prix achat<b>{{ formatMoney(g.pa) }}</b></div>
                            <div>Marge latente<b :class="g.ml < 0 ? 'vs-neg' : 'vs-pos'">{{ formatMoney(g.ml) }}</b></div>
                        </div>
                    </div>
                    <div v-if="lignesFiltrees.length" class="vs-mtot">
                        <div>Valeur achat<b>{{ formatMoney(totauxSelection.va) }}</b></div>
                        <div>Valeur vente<b>{{ formatMoney(totauxSelection.vv) }}</b></div>
                        <div>Marge latente<b>{{ formatMoney(totauxSelection.vv - totauxSelection.va) }}</b></div>
                        <div>Stock<b>{{ formatNombre(totauxSelection.q) }}</b></div>
                    </div>
                </div>

                <div class="vs-pager">
                    <span v-if="lignesFiltrees.length">{{ (page - 1) * parPage + 1 }} à {{ Math.min(page * parPage, lignesFiltrees.length) }} sur {{ lignesFiltrees.length }}</span>
                    <span v-else>0 résultat</span>
                    <span class="vs-pager__pg">
                        <button type="button" :disabled="page <= 1" @click="page--">‹</button>
                        <button v-for="p in pagesVisibles" :key="p" type="button" :class="{ 'vs-pager--on': p === page }" @click="page = p">{{ p }}</button>
                        <button type="button" :disabled="page >= nbPages" @click="page++">›</button>
                    </span>
                </div>
            </template>
            <div v-else class="vs-card__body"><div v-for="n in 8" :key="n" class="vs-skel"></div></div>

            <!-- table d'export (cachée) : toute la sélection, chiffres bruts -->
            <table ref="exportable_table" class="vs-hidden">
                <thead>
                <tr><th>{{ regroupement === 'cat' ? 'Catalogue' : 'Article' }}</th><th>Détail</th><th>Classe</th><th>Stock</th><th>Prix achat</th><th>Valeur achat</th><th>Prix vente</th><th>Valeur vente</th><th>Marge latente</th></tr>
                </thead>
                <tbody>
                <tr v-for="g in lignesFiltrees" :key="'x' + g.cle">
                    <td>{{ g.lib }}</td><td>{{ g.meta }}</td><td>{{ g.abc }}</td><td>{{ g.q }}</td><td>{{ arrondi(g.pa) }}</td><td>{{ arrondi(g.va) }}</td><td>{{ arrondi(g.pv) }}</td><td>{{ arrondi(g.vv) }}</td><td>{{ arrondi(g.ml) }}</td>
                </tr>
                </tbody>
                <tfoot>
                <tr><td>TOTAL</td><td></td><td></td><td>{{ totauxSelection.q }}</td><td></td><td>{{ arrondi(totauxSelection.va) }}</td><td></td><td>{{ arrondi(totauxSelection.vv) }}</td><td>{{ arrondi(totauxSelection.vv - totauxSelection.va) }}</td></tr>
                </tfoot>
            </table>
        </div>

    </div>

</template>

<script>

    import {currencyFormatDE, currentDateTime, ExportExcel, randomString} from "@/allfunction";
    import * as JwtService from "@/common/jwt.service";
    import VueApexCharts from "vue3-apexcharts";
    const axios = require('axios');
    import { avecCacheTB } from "@/service/cacheTableauDeBord";
    import bandeaucachetb from "@/components/views/pages/tableaudebord/bandeaucachetb";

    const COULEURS = ['#7c5ae0','#2563eb','#059669','#d97706','#db2777','#0891b2','#65a30d','#9333ea'];
    const SANS_CATALOGUE = 'Sans catalogue';

    export default {
        /* eslint-disable */
        name: "valorisationstock",
        components: {
            bandeaucachetb,
            apexcharts: VueApexCharts,
        },
        data() {
            return {
                depuisCache: false,
                heureCache: '',
                initcomponnent: false,
                chargement: false,
                charge: false,
                derniereMaj: '',

                /* lignes brutes : une par article et par magasin */
                lignes: [],
                magasin: '',

                recherche: '',
                regroupements: [
                    { code: 'ligne', label: 'Par magasin' },
                    { code: 'article', label: 'Par article' },
                    { code: 'cat', label: 'Par catalogue' },
                ],
                regroupement: 'ligne',
                pastilles: [
                    { code: 'tous', label: 'Tous', cls: '' },
                    { code: 'a', label: 'Classe A', cls: 'vs-chip--a' },
                    { code: 'sanspa', label: "Sans prix d'achat", cls: 'vs-chip--warn' },
                    { code: 'sanspv', label: 'Sans prix de vente', cls: 'vs-chip--bad' },
                    { code: 'neg', label: 'Stock négatif', cls: 'vs-chip--bad' },
                ],
                pastille: 'tous',
                masquerZero: true,
                colonnes: [
                    { k: 'lib', tri: 'lib', l: 'Article' },
                    { k: 'q', tri: 'q', l: 'Stock', r: 1 },
                    { k: 'pa', tri: 'pa', l: 'Prix achat', r: 1, g: 'a' },
                    { k: 'va', tri: 'va', l: 'Valeur achat', r: 1, g: 'a' },
                    { k: 'pv', tri: 'pv', l: 'Prix vente', r: 1, g: 'v' },
                    { k: 'vv', tri: 'vv', l: 'Valeur vente', r: 1, g: 'v' },
                    { k: 'ml', tri: 'ml', l: 'Marge latente', r: 1, g: 'm' },
                    { k: 'part', tri: 'va', l: 'Part', r: 1 },
                ],
                tri: 'va',
                sens: -1,
                page: 1,
                parPage: 25,
                couleurAbc: { A: '#7c5ae0', B: '#2563eb', C: '#94a3b8' },
            };
        },
        computed: {
            dateDuJour() {
                return new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            },

            magasins() {
                return [...new Set(this.lignes.map(l => l.nommaga))].sort();
            },

            /* Lignes du magasin choisi, valorisées (stock négatif compté à 0) et classées A/B/C. */
            lignesMagasin() {
                const rows = (this.magasin ? this.lignes.filter(l => l.nommaga === this.magasin) : this.lignes).map(l => {
                    const qp = Math.max(0, l.stock);
                    return {
                        mag: l.nommaga, art: l.libelle, cat: l.catalogue || SANS_CATALOGUE,
                        q: l.stock, pa: l.pa, pv: l.pv, va: qp * l.pa, vv: qp * l.pv, abc: 'C',
                    };
                });
                const total = rows.reduce((s, r) => s + r.va, 0);
                let cumul = 0;
                rows.slice().sort((a, b) => b.va - a.va).forEach(r => {
                    const avant = cumul;
                    cumul += r.va;
                    r.abc = r.va <= 0 ? 'C' : avant < total * 0.8 ? 'A' : avant < total * 0.95 ? 'B' : 'C';
                });
                return rows;
            },

            totaux() {
                const t = { va: 0, vv: 0, q: 0, refs: 0, sanspa: 0, sanspv: 0, neg: 0 };
                for (const r of this.lignesMagasin) {
                    t.va += r.va; t.vv += r.vv;
                    if (r.q > 0) { t.q += r.q; t.refs++; if (!r.pa) t.sanspa++; if (!r.pv) t.sanspv++; }
                    if (r.q < 0) t.neg++;
                }
                return t;
            },

            parMagasin() {
                const m = {};
                for (const r of this.lignesMagasin) m[r.mag] = (m[r.mag] || 0) + r.va;
                return Object.entries(m).map(([n, v]) => ({ n, v: Math.round(v) })).filter(x => x.v > 0).sort((a, b) => b.v - a.v);
            },
            parCatalogue() {
                const m = {};
                for (const r of this.lignesMagasin) m[r.cat] = (m[r.cat] || 0) + r.va;
                return Object.entries(m).map(([n, v]) => ({ n, v: Math.round(v) })).filter(x => x.v > 0).sort((a, b) => b.v - a.v);
            },
            syntheseAbc() {
                const c = { A: { n: 0, v: 0 }, B: { n: 0, v: 0 }, C: { n: 0, v: 0 } };
                const pos = this.lignesMagasin.filter(r => r.va > 0);
                pos.forEach(r => { c[r.abc].n++; c[r.abc].v += r.va; });
                const textes = { A: 'Le cœur de la valeur : à surveiller en priorité', B: 'Valeur intermédiaire', C: 'Petite valeur : vérifier les dormants' };
                return ['A', 'B', 'C'].map(k => ({
                    cle: k, n: c[k].n, texte: textes[k],
                    partN: pos.length ? Math.round(c[k].n / pos.length * 100) : 0,
                    partV: this.totaux.va ? Math.round(c[k].v / this.totaux.va * 100) : 0,
                }));
            },

            /* Regroupement du tableau. */
            groupes() {
                let rows;
                if (this.regroupement === 'ligne') {
                    rows = this.lignesMagasin.map(r => ({
                        cle: r.art + '|' + r.mag, lib: r.art, meta: r.mag + (r.cat !== SANS_CATALOGUE ? ' · ' + r.cat : ''),
                        q: r.q, pa: r.pa, pv: r.pv, va: r.va, vv: r.vv, abc: r.abc,
                        sanspa: r.q > 0 && !r.pa, sanspv: r.q > 0 && !r.pv, neg: r.q < 0,
                    }));
                } else {
                    const m = {};
                    for (const r of this.lignesMagasin) {
                        const k = this.regroupement === 'article' ? r.art : r.cat;
                        const g = m[k] || (m[k] = { cle: k, lib: k, q: 0, qp: 0, va: 0, vv: 0, n: 0, mags: {}, cat: r.cat, abc: 'C', sanspa: false, sanspv: false, neg: false });
                        g.q += r.q; g.qp += Math.max(0, r.q); g.va += r.va; g.vv += r.vv; g.n++; g.mags[r.mag] = 1;
                        if (r.q > 0 && !r.pa) g.sanspa = true;
                        if (r.q > 0 && !r.pv) g.sanspv = true;
                        if (r.q < 0) g.neg = true;
                        if (r.abc < g.abc) g.abc = r.abc;
                    }
                    rows = Object.values(m).map(g => {
                        const nm = Object.keys(g.mags).length;
                        g.pa = g.qp ? g.va / g.qp : 0;
                        g.pv = g.qp ? g.vv / g.qp : 0;
                        g.meta = this.regroupement === 'article'
                            ? nm + ' magasin' + (nm > 1 ? 's' : '') + (g.cat !== SANS_CATALOGUE ? ' · ' + g.cat : '')
                            : g.n + ' ligne' + (g.n > 1 ? 's' : '');
                        return g;
                    });
                }
                rows.forEach(g => { g.ml = g.vv - g.va; });
                return this.masquerZero ? rows.filter(g => g.q !== 0) : rows;
            },

            groupesRecherche() {
                const q = this.recherche.trim().toLowerCase();
                return q ? this.groupes.filter(g => (g.lib + ' ' + g.meta).toLowerCase().includes(q)) : this.groupes;
            },

            compteurs() {
                const c = {};
                for (const p of this.pastilles) c[p.code] = this.groupesRecherche.filter(g => this.passePastille(g, p.code)).length;
                return c;
            },

            lignesFiltrees() {
                const rows = this.groupesRecherche.filter(g => this.passePastille(g, this.pastille));
                const k = this.tri, s = this.sens;
                rows.sort((a, b) => k === 'lib' ? s * a.lib.localeCompare(b.lib) : s * (a[k] - b[k]));
                return rows;
            },

            totauxSelection() {
                return this.lignesFiltrees.reduce((a, g) => { a.q += g.q; a.va += g.va; a.vv += g.vv; return a; }, { q: 0, va: 0, vv: 0 });
            },

            nbPages() {
                return Math.max(1, Math.ceil(this.lignesFiltrees.length / this.parPage));
            },
            lignesPage() {
                const p = Math.min(this.page, this.nbPages);
                return this.lignesFiltrees.slice((p - 1) * this.parPage, p * this.parPage);
            },
            pagesVisibles() {
                let st = Math.max(1, this.page - 2);
                const en = Math.min(this.nbPages, st + 4);
                st = Math.max(1, en - 4);
                const r = [];
                for (let i = st; i <= en; i++) r.push(i);
                return r;
            },

            /* ---------- graphiques ---------- */
            optionsMagasin() {
                return {
                    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    colors: ['#f59e0b'],
                    dataLabels: { enabled: false },
                    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '55%' } },
                    grid: { borderColor: '#e2e8f0', strokeDashArray: 3 },
                    xaxis: { categories: this.parMagasin.map(x => x.n.length > 22 ? x.n.slice(0, 22) + '…' : x.n), labels: { formatter: v => this.abrege(v), style: { colors: '#94a3b8' } } },
                    tooltip: { y: { formatter: v => this.formatMoney(v) + ' F' } },
                };
            },
            optionsCatalogue() {
                return {
                    chart: { type: 'donut', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    labels: this.parCatalogue.map(x => x.n),
                    colors: COULEURS,
                    legend: { position: 'right', fontSize: '11px' },
                    dataLabels: { enabled: true, formatter: v => v.toFixed(0) + ' %' },
                    plotOptions: { pie: { donut: { size: '60%' } } },
                    tooltip: { y: { formatter: v => this.formatMoney(v) + ' F' } },
                };
            },
        },
        methods: {

            loadinitcomponnent() {
                if (this.initcomponnent === false) {
                    this.charger();
                    this.initcomponnent = true;
                }
            },

            async charger(force) {
                if (this.chargement) return;
                this.chargement = true;
                const url = process.env.VUE_APP_API_URL + '/egl745874tb771714dfe/dashboard/valorisation/' + this.$store.state.appsociete + '/' + randomString();
                try {
                    const r = await avecCacheTB(this.$store.state.appsociete, 'valorisation', {}, async () => {
                        const res = await axios.post(url, {}, {
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + JwtService.getToken(),
                                'csrftoken': this.$store.state.appsociete
                            }, withCredentials: true
                        });
                        return res.data.success && res.data.m ? (res.data.m.lignes || []) : null;
                    }, { force: force === true });
                    this.lignes = Array.isArray(r.data) ? r.data : [];
                    this.depuisCache = r.cache;
                    this.heureCache = String(new Date(r.t).getHours()).padStart(2, '0') + ':' + String(new Date(r.t).getMinutes()).padStart(2, '0');
                    if (this.magasin && !this.magasins.includes(this.magasin)) this.magasin = '';
                    this.charge = true;
                    this.page = 1;
                    const h = new Date(r.t);
                    this.derniereMaj = String(h.getHours()).padStart(2, '0') + ':' + String(h.getMinutes()).padStart(2, '0');
                } catch (e) {
                    toastr['error']('Une erreur est subvenue. <br/> veuillez actualiser la page.', '<b></b>');
                } finally {
                    this.chargement = false;
                }
            },

            passePastille(g, code) {
                if (code === 'a') return g.abc === 'A';
                if (code === 'sanspa') return g.sanspa;
                if (code === 'sanspv') return g.sanspv;
                if (code === 'neg') return g.neg;
                return true;
            },

            choisirPastille(code) {
                this.pastille = code;
                /* un stock négatif n'est pas un stock à 0, mais on rend visibles toutes les lignes concernées */
                if (code === 'neg') this.masquerZero = false;
                this.page = 1;
            },

            trier(k) {
                if (this.tri === k) this.sens = -this.sens;
                else { this.tri = k; this.sens = k === 'lib' ? 1 : -1; }
                this.page = 1;
            },

            exporterExcel() {
                ExportExcel('xlsx', 'valorisation_stock_' + currentDateTime(), undefined, undefined, this.$refs.exportable_table);
            },

            echapper(s) {
                return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
            },

            /* Surligne la recherche (texte échappé avant insertion). */
            surligner(texte) {
                const s = String(texte || '');
                const q = this.recherche.trim();
                if (!q) return this.echapper(s);
                const i = s.toLowerCase().indexOf(q.toLowerCase());
                if (i < 0) return this.echapper(s);
                return this.echapper(s.slice(0, i)) + '<mark>' + this.echapper(s.slice(i, i + q.length)) + '</mark>' + this.echapper(s.slice(i + q.length));
            },

            formatMoney(v) {
                return currencyFormatDE(Math.round(Number(v) || 0));
            },
            /* Quantités et compteurs : séparateur de milliers, sans devise. */
            formatNombre(v) {
                return (Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 }).replace(/[  ]/g, ' ');
            },
            formatPourcent(v) {
                return (Number(v) || 0).toFixed(1).replace('.', ',');
            },
            arrondi(v) {
                return Math.round((Number(v) || 0) * 100) / 100;
            },
            abrege(v) {
                const a = Math.abs(v);
                if (a >= 1e6) return (v / 1e6).toFixed(1).replace('.0', '') + ' M';
                if (a >= 1e3) return Math.round(v / 1e3) + ' k';
                return Math.round(v);
            },

            raccourciRecherche(e) {
                const tag = (e.target && e.target.tagName) || '';
                if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && this.$el.offsetParent !== null) {
                    e.preventDefault();
                    this.$refs.recherche && this.$refs.recherche.focus();
                }
            },
        },
        watch: {
            recherche() { this.page = 1; },
            magasin() { this.page = 1; },
        },
        mounted() {
            document.addEventListener('keydown', this.raccourciRecherche);
        },
        beforeUnmount() {
            document.removeEventListener('keydown', this.raccourciRecherche);
        },
    }
</script>

<style scoped>

.vs-page { padding-bottom: 1rem; }

/* filtres */
.vs-filters { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.vs-input { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.38rem 0.6rem; font-size: 0.8rem; color: var(--cf-ink); max-width: 100%; }
.vs-btn { height: 2.1rem; padding: 0 0.7rem; display: inline-flex; gap: 0.35rem; align-items: center; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); color: var(--cf-muted); cursor: pointer; font-size: 0.8rem; font-weight: 500; }
.vs-btn:hover:not(:disabled) { color: rgb(var(--primary)); }
.vs-btn:disabled { opacity: 0.5; cursor: default; }
.vs-btn--xls { color: #047857; }
.vs-spin { display: inline-block; animation: vs-rot 0.9s linear infinite; }
@keyframes vs-rot { to { transform: rotate(360deg); } }
.vs-seg { display: inline-flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 3px; gap: 2px; }
.vs-seg button { border: 0; background: transparent; font-size: 0.78rem; font-weight: 500; padding: 0.28rem 0.65rem; border-radius: var(--cf-radius-xs); color: var(--cf-muted); cursor: pointer; white-space: nowrap; }
.vs-seg button:hover { color: rgb(var(--primary)); }
.vs-seg button.vs-seg--on { background: rgb(var(--primary)); color: #fff; }

/* KPI */
.vs-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
@media (min-width: 1100px) { .vs-kpis { grid-template-columns: 1.25fr 1fr 1fr 1fr 1fr; } }
.vs-kpi { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); padding: 0.9rem 1rem; min-width: 0; transition: box-shadow 0.15s, transform 0.15s; }
.vs-kpi:hover { box-shadow: var(--cf-shadow-md); transform: translateY(-1px); }
.vs-kpi__lbl { display: flex; justify-content: space-between; align-items: center; color: var(--cf-muted); font-size: 0.78rem; font-weight: 500; }
.vs-kpi__lbl i { width: 1.9rem; height: 1.9rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 1rem; }
.vs-kpi__val { font-size: 1.4rem; font-weight: 800; color: var(--cf-ink-strong); margin: 0.35rem 0 0.1rem; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.vs-kpi__val small { font-size: 0.75rem; font-weight: 500; color: var(--cf-muted); }
.vs-kpi__sub { font-size: 0.72rem; color: var(--cf-muted); }
.vs-kpi--main { background: linear-gradient(135deg, #d97706 0%, #b45309 100%); border: 0; color: #fff; }
.vs-kpi--main .vs-kpi__lbl, .vs-kpi--main .vs-kpi__sub { color: rgb(255 255 255 / 0.88); }
.vs-kpi--main .vs-kpi__lbl i { background: rgb(255 255 255 / 0.2); }
.vs-kpi--main .vs-kpi__val, .vs-kpi--main .vs-kpi__val small { color: #fff; }
.vs-kpi--warn { border-left: 4px solid #d97706; cursor: pointer; }
.vs-i-vio { background: #f1ecfd; color: #7c5ae0; }
.vs-i-grn { background: #ecfdf5; color: #059669; }
.vs-i-blue { background: #eff6ff; color: #2563eb; }
.vs-i-amb { background: #fff7ed; color: #d97706; }
.vs-split { display: flex; height: 8px; border-radius: 99px; overflow: hidden; margin-top: 0.5rem; background: var(--cf-surface-alt); }
.vs-split i { display: block; height: 100%; }
.vs-pos { color: #059669; font-weight: 600; }
.vs-neg { color: #dc2626; font-weight: 600; }
.vs-faint { color: var(--cf-faint); }

/* cartes */
.vs-g3 { display: grid; gap: 1rem; margin-bottom: 1rem; }
@media (min-width: 1024px) { .vs-g3 { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.vs-card { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); min-width: 0; }
.vs-card__head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.75rem 1rem; border-bottom: 1px solid var(--cf-border); }
.vs-card__head h2 { margin: 0; font-size: 0.9rem; font-weight: 700; color: var(--cf-ink-strong); display: flex; gap: 0.4rem; align-items: center; }
.vs-card__head h2 i { color: rgb(var(--primary)); }
.vs-card__head h2 span { font-size: 0.75rem; font-weight: 400; color: var(--cf-muted); }
.vs-card__body { padding: 0.75rem 1rem; }

/* ABC */
.vs-abc { display: grid; gap: 0.6rem; margin-top: 0.2rem; }
.vs-abc__row { display: grid; grid-template-columns: 2rem minmax(0, 1fr); gap: 0.6rem; align-items: center; }
.vs-abc__l { width: 2rem; height: 2rem; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; color: #fff; }
.vs-abc__t { font-size: 0.78rem; color: var(--cf-muted); }
.vs-abc__t b { color: var(--cf-ink-strong); }
.vs-abc__t--sm { font-size: 0.7rem; }
.vs-abc__bar { display: flex; height: 7px; border-radius: 99px; overflow: hidden; background: var(--cf-surface-alt); margin-top: 0.25rem; }
.vs-abc__bar i { display: block; height: 100%; }

/* barre d'outils */
.vs-toolbar { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; padding: 0.85rem 1rem; border-bottom: 1px solid var(--cf-border); }
.vs-search { position: relative; flex: 1 1 280px; min-width: 200px; }
.vs-search > i { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--cf-faint); font-size: 1rem; }
.vs-search input { width: 100%; padding: 0.55rem 2.2rem; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); font-size: 0.85rem; background: var(--cf-surface); }
.vs-search input:focus { outline: none; border-color: rgb(var(--primary)); background: #fff; box-shadow: 0 0 0 3px rgb(var(--primary) / 0.12); }
.vs-search kbd { position: absolute; right: 0.6rem; top: 50%; transform: translateY(-50%); font-size: 0.68rem; color: var(--cf-faint); border: 1px solid var(--cf-border); border-radius: 4px; padding: 0 5px; background: #fff; }

/* pastilles */
.vs-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0.6rem 1rem; border-bottom: 1px solid var(--cf-border); background: var(--cf-surface); align-items: center; }
.vs-chip { border: 1px solid var(--cf-border); background: #fff; border-radius: 999px; padding: 0.25rem 0.7rem; font-size: 0.76rem; font-weight: 500; color: var(--cf-muted); cursor: pointer; display: inline-flex; gap: 0.35rem; align-items: center; }
.vs-chip b { background: var(--cf-surface-alt); color: var(--cf-ink); border-radius: 999px; padding: 0 0.45rem; font-size: 0.7rem; }
.vs-chip--a { color: #7c5ae0; }
.vs-chip--warn { color: #d97706; }
.vs-chip--bad { color: #dc2626; }
.vs-chip.vs-chip--on { background: rgb(var(--primary)); border-color: rgb(var(--primary)); color: #fff; }
.vs-chip.vs-chip--on b { background: rgb(255 255 255 / 0.25); color: #fff; }
.vs-toggle { display: inline-flex; gap: 0.4rem; align-items: center; font-size: 0.76rem; color: var(--cf-muted); cursor: pointer; margin-left: 0.25rem; }
.vs-chips__sep { flex: 1; }
.vs-resume { font-size: 0.76rem; color: var(--cf-muted); }

/* tableau */
.vs-table-wrap { overflow: auto; max-height: 620px; }
.vs-table-wrap table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.82rem; }
.th { position: sticky; top: 0; z-index: 2; padding: 0.65rem 0.85rem; font-weight: 600; font-size: 0.72rem; color: var(--cf-ink); background: var(--cf-surface-alt); border-bottom: 1px solid var(--cf-border); white-space: nowrap; text-align: left; cursor: pointer; user-select: none; }
.th:hover { color: rgb(var(--primary)); }
.th--right { text-align: right; }
.th__s { opacity: 0.35; margin-left: 2px; }
.th--sorted .th__s { opacity: 1; color: rgb(var(--primary)); }
.th--a { border-top: 3px solid #f59e0b; }
.th--v { border-top: 3px solid #7c5ae0; }
.th--m { border-top: 3px solid #059669; }
.td { padding: 0.6rem 0.85rem; border-bottom: 1px solid var(--cf-surface-alt); white-space: nowrap; font-variant-numeric: tabular-nums; vertical-align: middle; color: var(--cf-ink); }
.td--right { text-align: right; }
.vs-row:hover .td { background: #faf8ff; }
.vs-ca { background: #fffcf5; }
.vs-cv { background: #faf8ff; }
.vs-art { display: flex; gap: 0.6rem; align-items: center; min-width: 220px; white-space: normal; }
.vs-abcb { flex: 0 0 auto; width: 1.6rem; height: 1.6rem; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; font-size: 0.72rem; color: #fff; }
.vs-art__name { font-weight: 600; color: var(--cf-ink-strong); line-height: 1.2; }
.vs-art__meta { font-size: 0.7rem; color: var(--cf-muted); }
.vs-art :deep(mark) { background: #fef08a; color: inherit; border-radius: 2px; padding: 0 1px; }
.vs-tag { display: inline-block; font-size: 0.64rem; font-weight: 700; padding: 1px 6px; border-radius: 999px; margin-left: 4px; vertical-align: middle; }
.vs-tag--pa { background: #fff7ed; color: #d97706; }
.vs-tag--bad { background: #fef2f2; color: #dc2626; }
.vs-part { display: inline-flex; align-items: center; gap: 0.4rem; justify-content: flex-end; }
.vs-part span { width: 46px; height: 6px; border-radius: 99px; background: var(--cf-surface-alt); overflow: hidden; display: inline-block; }
.vs-part span i { display: block; height: 100%; border-radius: 99px; background: #f59e0b; }
tfoot .td { position: sticky; bottom: 0; z-index: 2; background: #1e293b; color: #fff; font-weight: 700; border: 0; }
.vs-foot__lbl { font-size: 0.68rem; font-weight: 500; color: #cbd5e1; display: block; }

/* pagination */
.vs-pager { display: flex; flex-wrap: wrap; gap: 0.5rem; justify-content: space-between; align-items: center; padding: 0.7rem 1rem; border-top: 1px solid var(--cf-border); font-size: 0.78rem; color: var(--cf-muted); }
.vs-pager__pg { display: flex; gap: 0.25rem; align-items: center; }
.vs-pager__pg button { min-width: 2rem; height: 2rem; border: 1px solid var(--cf-border); background: #fff; border-radius: var(--cf-radius-xs); cursor: pointer; font-size: 0.78rem; color: var(--cf-ink); }
.vs-pager__pg button.vs-pager--on { background: rgb(var(--primary)); border-color: rgb(var(--primary)); color: #fff; }
.vs-pager__pg button:disabled { opacity: 0.4; cursor: default; }

/* états */
.vs-empty { text-align: center; padding: 2.5rem 1rem; color: var(--cf-muted); font-size: 0.85rem; }
.vs-empty i { font-size: 2rem; color: var(--cf-faint); display: block; margin-bottom: 0.4rem; }
.vs-skel { height: 0.75rem; border-radius: 6px; margin: 0.55rem 0; background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%); background-size: 200% 100%; animation: vs-sh 1.3s infinite; }
.vs-skel--lg { height: 1.4rem; }
.vs-skel--chart { height: 210px; }
@keyframes vs-sh { to { background-position: -200% 0; } }
.vs-hidden { display: none; }

/* mobile : lignes en cartes */
.vs-only-mobile { display: none; }
.vs-mcards { gap: 0.6rem; padding: 0.75rem; }
.vs-mcard { border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.75rem; }
.vs-mcard__g { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.45rem 0.8rem; margin-top: 0.6rem; font-size: 0.72rem; color: var(--cf-muted); }
.vs-mcard__g b { display: block; color: var(--cf-ink-strong); font-size: 0.82rem; font-variant-numeric: tabular-nums; }
.vs-mtot { border-radius: var(--cf-radius-sm); background: #1e293b; color: #fff; padding: 0.75rem; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.4rem; font-size: 0.75rem; }
.vs-mtot b { display: block; font-size: 0.9rem; }
@media (max-width: 760px) {
    .vs-only-desktop { display: none; }
    .vs-only-mobile { display: grid; }
    .vs-kpi__val { font-size: 1.1rem; }
}

</style>
